// JSON Pointer (RFC 6901) operations for A2UI data binding

/**
 * Parse a JSON Pointer string into path segments.
 * Handles RFC 6901 escaping (~0 for ~, ~1 for /).
 */
export const parseJsonPointer = (pointer: string): string[] => {
    if (pointer === '' || pointer === '/') return []
    if (!pointer.startsWith('/')) {
        throw new Error(`Invalid JSON Pointer: must start with '/' - got '${pointer}'`)
    }
    return pointer.slice(1).split('/').map((segment) =>
        segment.replace(/~1/g, '/').replace(/~0/g, '~'))
}

/**
 * Get a value from an object using a JSON Pointer.
 */
export const getByPointer = <T = unknown>(
    obj: Record<string, unknown>,
    pointer: string,
): T | undefined => {
    if (pointer === '' || pointer === '/') return obj as T
    const segments = parseJsonPointer(pointer)
    let current: unknown = obj
    for (const segment of segments) {
        if (current === null || current === undefined) return undefined
        if (typeof current === 'object') {
            current = (current as Record<string, unknown>)[segment]
        } else {
            return undefined
        }
    }
    return current as T
}

/**
 * Set a value in an object using a JSON Pointer (immutable - returns new object).
 */
export const setByPointer = <T extends Record<string, unknown>>(
    obj: T,
    pointer: string,
    value: unknown,
): T => {
    if (pointer === '' || pointer === '/') return value as T
    const segments = parseJsonPointer(pointer)
    const result = { ...obj }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let current: any = result

    const isNumericSegment = (s: string): boolean => /^\d+$/.test(s)

    for (let i = 0; i < segments.length - 1; i++) {
        const segment = segments[i]
        const nextSegment = segments[i + 1]

        if (current[segment] === undefined || current[segment] === null) {
            // Create array if next segment is numeric, otherwise object
            current[segment] = isNumericSegment(nextSegment) ? [] : {}
        }

        // Clone preserving array vs object semantics
        if (Array.isArray(current[segment])) {
            current[segment] = [...current[segment]]
        } else {
            current[segment] = { ...(current[segment] as Record<string, unknown>) }
        }
        current = current[segment]
    }

    // Final assignment - use numeric index if parent is array
    const finalSegment = segments[segments.length - 1]
    if (Array.isArray(current) && isNumericSegment(finalSegment)) {
        current[parseInt(finalSegment, 10)] = value
    } else {
        current[finalSegment] = value
    }

    return result
}

/**
 * Client-side validation functions for A2UI checks.
 * Each function returns true if validation passes, false if it fails.
 */
export const validationFunctions: Record<string, (value: unknown, args: Record<string, unknown>) => boolean> = {
    required: (value) => {
        if (value === null || value === undefined) return false
        if (typeof value === 'string') return value.trim().length > 0
        if (Array.isArray(value)) return value.length > 0
        return true
    },
    email: (value) => typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    regex: (value, args) => typeof value === 'string' && new RegExp(args.pattern as string).test(value),
    minLength: (value, args) => {
        const min = Number(args.min)
        return typeof value === 'string' ? value.length >= min : Array.isArray(value) ? value.length >= min : true
    },
    maxLength: (value, args) => {
        const max = Number(args.max)
        return typeof value === 'string' ? value.length <= max : Array.isArray(value) ? value.length <= max : true
    },
}

/** Validation error returned from executeValidationChecks. */
export type ValidationError = {
    message: string
}

/** FunctionCall structure matching the A2UI proto definition. */
export type FunctionCallCheck = {
    call: string
    args: Record<string, string>
    message: string
}

/**
 * Executes a list of FunctionCall validation checks against the data model.
 * Returns an array of ValidationError for each failed check.
 */
export const executeValidationChecks = (
    checks: FunctionCallCheck[],
    dataModel: Record<string, unknown>,
): ValidationError[] => {
    const errors: ValidationError[] = []

    for (const check of checks) {
        const fn = validationFunctions[check.call]
        if (!fn) {
            continue
        }

        // Resolve the value argument - can be a path or literal
        const valuePath = check.args.value
        let value: unknown = valuePath
        if (valuePath?.startsWith('/')) {
            value = getByPointer(dataModel, valuePath)
        }

        // Build args object (excluding 'value' which is resolved separately)
        const args: Record<string, unknown> = {}
        for (const [k, v] of Object.entries(check.args)) {
            if (k === 'value') continue
            args[k] = v
        }

        const isValid = fn(value, args)
        if (!isValid) {
            errors.push({ message: check.message || `Validation failed: ${check.call}` })
        }
    }

    return errors
}

/** ValidationCheck structure with oneofKind for nested AND/OR logic. */
export type ValidationCheckType = {
    check: {
        oneofKind: 'call'
        call: FunctionCallCheck
    } | {
        oneofKind: 'and'
        and: { checks: ValidationCheckType[] }
    } | {
        oneofKind: 'or'
        or: { checks: ValidationCheckType[] }
    }
}

/**
 * Evaluates a ValidationCheck (with potential AND/OR nesting) against the data model.
 * Returns true if the check passes, false if it fails.
 */
export const evaluateValidationCheck = (
    check: ValidationCheckType,
    dataModel: Record<string, unknown>,
): boolean => {
    switch (check.check.oneofKind) {
        case 'call': {
            const errors = executeValidationChecks([check.check.call], dataModel)
            return errors.length === 0
        }
        case 'and': {
            return check.check.and.checks.every((c) => evaluateValidationCheck(c, dataModel))
        }
        case 'or': {
            return check.check.or.checks.some((c) => evaluateValidationCheck(c, dataModel))
        }
        default:
            return true
    }
}

/**
 * Evaluates an array of ValidationCheck (for button auto-disable).
 * All checks must pass for the result to be true.
 */
export const evaluateButtonChecks = (
    checks: ValidationCheckType[],
    dataModel: Record<string, unknown>,
): boolean => {
    return checks.every((check) => evaluateValidationCheck(check, dataModel))
}
